import { z } from 'zod';
import { TASK_STEPS_MAX } from '../tasks.js';
import { TaskDescription, TaskStepLabel } from './actions.js';
import { IsoDate, IsoDateTime, Uuid } from './common.js';

/**
 * Assistant, Conseiller, conseil du jour (lot E2, CDC §0 « IA », §3.7 `POST /api/assistant/chat`,
 * `GET /api/assistant/weekly`). Règle cardinale : l'assistant LIT les données et n'exécute JAMAIS rien.
 * Il peut seulement PROPOSER une action ; l'interface la transforme en plan habituel (écran de validation)
 * ou en tâche manuelle préremplie, que l'utilisateur valide lui-même.
 */

// ------------------------------------------------------------------ Propositions (boutons)

/**
 * Plans qu'une proposition peut ouvrir : ceux qui se construisent à partir d'une action ou d'un projet
 * existants. Le serveur reconstruit toujours le plan à partir de l'état réel (`POST /api/plans`).
 */
export const PROPOSABLE_PLAN_KINDS = [
  'reminder.email',
  'invoice.create',
  'quote.create',
  'meeting.propose',
  'action.done',
  'project.shift',
  'message.send',
] as const;
export const ProposablePlanKind = z.enum(PROPOSABLE_PLAN_KINDS);
export type ProposablePlanKind = z.infer<typeof ProposablePlanKind>;

const ActionPlanRefs = z.object({ actionId: Uuid }).strict();

/** Références attendues par chaque plan proposable (mêmes champs que les builders). */
export const PROPOSAL_REFS_SCHEMAS: Record<ProposablePlanKind, z.ZodType> = {
  'reminder.email': ActionPlanRefs,
  'invoice.create': ActionPlanRefs,
  'quote.create': ActionPlanRefs,
  'meeting.propose': ActionPlanRefs,
  'action.done': ActionPlanRefs,
  'project.shift': z
    .object({
      projectId: Uuid,
      days: z
        .int()
        .min(-365)
        .max(365)
        .refine((d) => d !== 0, { message: 'Décalage nul' }),
    })
    .strict(),
  'message.send': z
    .object({
      text: z.string().trim().min(1).max(4_000),
      toUserId: Uuid.optional(),
      prefer: z.enum(['dm', 'group']).optional(),
    })
    .strict(),
};

const ProposalLabel = z.string().trim().min(1).max(80);

/** Bouton « ouvrir le plan » : l'écran de validation habituel, rien n'est exécuté avant « Valider ». */
export const PlanProposal = z.object({
  kind: z.literal('plan'),
  label: ProposalLabel,
  planKind: ProposablePlanKind,
  refs: z.record(z.string(), z.unknown()),
});
export type PlanProposal = z.infer<typeof PlanProposal>;

/** Bouton « ajouter une tâche » : le formulaire de tâche manuelle s'ouvre prérempli (POST /api/actions). */
export const TaskProposal = z.object({
  kind: z.literal('task'),
  label: ProposalLabel,
  title: z.string().trim().min(1).max(300),
  assigneeId: Uuid.optional(),
  dueDate: IsoDate.optional(),
  clientId: Uuid.optional(),
  projectId: Uuid.optional(),
  /** Lot AS : description (texte brut) et étapes à cocher proposées, modifiables avant « Ajouter ». */
  description: TaskDescription.optional(),
  steps: z.array(TaskStepLabel).max(TASK_STEPS_MAX).optional(),
});
export type TaskProposal = z.infer<typeof TaskProposal>;

/**
 * Lot AD : bouton « Signaler ce problème » : le formulaire de retour (onglet Retours) s'ouvre prérempli et
 * modifiable ; c'est la personne qui l'envoie (`POST /api/feedback`, conversation liée). `type` : bug ou idée ;
 * `observed` : ce que l'assistant a constaté dans les données (une ou deux phrases).
 */
export const FEEDBACK_PROPOSAL_TYPES = ['bug', 'idea'] as const;
export const FeedbackProposal = z.object({
  kind: z.literal('feedback'),
  label: ProposalLabel,
  type: z.enum(FEEDBACK_PROPOSAL_TYPES),
  screen: z.string().trim().min(1).max(80),
  text: z.string().trim().min(1).max(5_000),
  observed: z.string().trim().min(1).max(2_000),
});
export type FeedbackProposal = z.infer<typeof FeedbackProposal>;

export const Proposal = z.discriminatedUnion('kind', [PlanProposal, TaskProposal, FeedbackProposal]);
export type Proposal = z.infer<typeof Proposal>;

/**
 * Valide une proposition reçue de Claude (outil `propose_action`) : forme générale, puis références
 * propres au plan. Renvoie la proposition normalisée ou le message d'erreur à renvoyer au modèle.
 */
export function validateProposal(
  input: unknown,
): { ok: true; proposal: Proposal } | { ok: false; error: string } {
  const parsed = Proposal.safeParse(input);
  if (!parsed.success) {
    return {
      ok: false,
      error: parsed.error.issues.map((i) => `${i.path.join('.') || '·'} : ${i.message}`).join(' ; '),
    };
  }
  const proposal = parsed.data;
  if (proposal.kind === 'plan') {
    const refs = PROPOSAL_REFS_SCHEMAS[proposal.planKind].safeParse(proposal.refs);
    if (!refs.success) {
      return {
        ok: false,
        error: `refs invalides pour ${proposal.planKind} : ${refs.error.issues.map((i) => `${i.path.join('.') || '·'} ${i.message}`).join(' ; ')}`,
      };
    }
    return { ok: true, proposal: { ...proposal, refs: refs.data as Record<string, unknown> } };
  }
  return { ok: true, proposal };
}

// ------------------------------------------------------------------ Conversation

export const AssistantMessage = z.object({
  role: z.enum(['user', 'assistant']),
  text: z.string(),
  /** Boutons proposés sous la réponse (jamais exécutés par l'assistant). */
  proposals: z.array(Proposal).default([]),
  /** Outils lus pour cette réponse (« a consulté : chiffres, projets »). */
  tools: z.array(z.string()).default([]),
  at: IsoDateTime,
});
export type AssistantMessage = z.infer<typeof AssistantMessage>;

/** `GET /api/assistant/conversations/:id` (propriétaire seulement). */
export const AssistantConversation = z.object({
  id: Uuid,
  messages: z.array(AssistantMessage),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime.nullable(),
});
export type AssistantConversation = z.infer<typeof AssistantConversation>;

export const ConversationIdParams = z.object({ id: Uuid });

/** Longueur maximale d'une question. */
export const ASSISTANT_MESSAGE_MAX_CHARS = 4_000;
/** Nombre maximal de questions dans une conversation (au-delà : en ouvrir une nouvelle). */
export const ASSISTANT_MAX_TURNS = 30;

/** `POST /api/assistant/chat` : réponse en flux SSE (événements ci-dessous). */
export const AssistantChatBody = z.object({
  conversationId: Uuid.optional(),
  message: z.string().trim().min(1).max(ASSISTANT_MESSAGE_MAX_CHARS),
  /**
   * Lot AD : bouton « Comment c'est calculé ? » : identifiant de la fiche du chiffre (Guide du Cockpit). Le
   * serveur refuse un identifiant inconnu (400 UNKNOWN_FIGURE).
   */
  figureId: z
    .string()
    .regex(/^[a-z0-9_.-]+$/)
    .max(80)
    .optional(),
});
export type AssistantChatBody = z.infer<typeof AssistantChatBody>;

/**
 * Événements SSE de `POST /api/assistant/chat`, dans l'ordre : `conversation` (une fois), puis des `tool`
 * (outil lu) et des `delta` (texte), enfin `done` (message complet, propositions comprises) ou `error`.
 */
export const AssistantStreamEvent = z.discriminatedUnion('type', [
  z.object({ type: z.literal('conversation'), conversationId: Uuid }),
  z.object({ type: z.literal('tool'), name: z.string(), label: z.string() }),
  z.object({ type: z.literal('delta'), text: z.string() }),
  z.object({ type: z.literal('done'), conversationId: Uuid, message: AssistantMessage }),
  z.object({ type: z.literal('error'), code: z.string(), message: z.string() }),
]);
export type AssistantStreamEvent = z.infer<typeof AssistantStreamEvent>;

// ------------------------------------------------------------------ Suggestions

/** `GET /api/assistant/suggestions` : questions proposées, calculées à partir des données du moment. */
export const AssistantSuggestions = z.object({ items: z.array(z.string()) });
export type AssistantSuggestions = z.infer<typeof AssistantSuggestions>;

// ------------------------------------------------------------------ Analyse du lundi

export const IdeaEffort = z.enum(['faible', 'moyen', 'élevé']);
export type IdeaEffort = z.infer<typeof IdeaEffort>;

export const AnalysisIdea = z.object({
  title: z.string().min(1).max(200),
  text: z.string().min(1).max(1_000),
  /** « +1 200 €/an si 10 passent » */
  gain: z.string().max(120),
  effort: IdeaEffort,
  /** Membre à qui confier la piste (null : la personne qui clique). */
  suggestedAssignee: Uuid.nullable(),
});
export type AnalysisIdea = z.infer<typeof AnalysisIdea>;

/** Sortie structurée demandée à Claude (et produite par le mode démo). */
export const WeeklyAnalysisContent = z.object({
  /** « Où vous en êtes » : un ou deux paragraphes. */
  summary: z.string().min(1).max(3_000),
  /** « Ce qui m'inquiète » : points courts, le premier mot-clé en gras côté écran (« titre. détail »). */
  concerns: z.array(z.object({ title: z.string().min(1).max(200), detail: z.string().max(600) })).max(8),
  ideas: z.array(AnalysisIdea).max(8),
  /** Le conseil du jour (et de la semaine sur le Mode écran), une ou deux phrases. */
  tipOfTheDay: z.string().min(1).max(400),
});
export type WeeklyAnalysisContent = z.infer<typeof WeeklyAnalysisContent>;

/** Une piste telle que l'écran la montre : avec l'action déjà créée, le cas échéant. */
export const WeeklyIdea = AnalysisIdea.extend({
  index: z.int().min(0),
  /** Tâche « À faire » créée depuis cette piste (bouton désactivé). */
  actionId: Uuid.nullable(),
});
export type WeeklyIdea = z.infer<typeof WeeklyIdea>;

export const WeeklyAnalysis = z.object({
  id: Uuid,
  /** Lundi de la semaine analysée. */
  weekOf: IsoDate,
  summary: z.string(),
  concerns: WeeklyAnalysisContent.shape.concerns,
  ideas: z.array(WeeklyIdea),
  tipOfTheDay: z.string(),
  /** `ai` : rédigée par Claude ; `demo` : analyse de démonstration (mode fake). */
  source: z.enum(['ai', 'demo']),
  model: z.string().nullable(),
  /** `cron` (lundi 7 h 30) ou `manual` (« Refaire l'analyse »). */
  trigger: z.enum(['cron', 'manual']),
  createdAt: IsoDateTime,
});
export type WeeklyAnalysis = z.infer<typeof WeeklyAnalysis>;

/** `GET /api/assistant/weekly` : la dernière analyse, ou null s'il n'y en a pas encore. */
export const WeeklyAnalysisResponse = z.object({ analysis: WeeklyAnalysis.nullable() });
export type WeeklyAnalysisResponse = z.infer<typeof WeeklyAnalysisResponse>;

/** `GET /api/assistant/tip` : « Le conseil du jour » (carte de À faire, diapositive du Mode écran). */
export const AssistantTip = z.object({
  text: z.string(),
  /** Date de l'analyse d'où vient le conseil, null pour le conseil par défaut. */
  analysisDate: IsoDate.nullable(),
  source: z.enum(['ai', 'demo', 'default']),
});
export type AssistantTip = z.infer<typeof AssistantTip>;

// ------------------------------------------------------------------ Récap du matin

/** Comptes du récap du matin d'un membre (logique pure, testée). */
export interface MorningRecapCounts {
  late: number;
  today: number;
}

/**
 * Récap du matin : actions encore ouvertes attribuées au membre, en retard (échéance passée) et du jour.
 * Les actions en attente de réponse du client ne comptent pas (rien à faire tant qu'il n'a pas répondu).
 */
export function morningRecapCounts(
  actions: readonly { dueDate: string; status: string }[],
  today: string,
): MorningRecapCounts {
  let late = 0;
  let todayCount = 0;
  for (const a of actions) {
    if (a.status !== 'OPEN' && a.status !== 'REPLY_TO_HANDLE') continue;
    if (a.dueDate < today) late += 1;
    else if (a.dueDate === today) todayCount += 1;
  }
  return { late, today: todayCount };
}

/** Texte de la notification « Tes actions du jour » (null : rien à signaler, pas de notification). */
export function morningRecapBody(counts: MorningRecapCounts): string | null {
  if (counts.late === 0 && counts.today === 0) return null;
  const parts: string[] = [];
  if (counts.today > 0) parts.push(`${counts.today} pour aujourd'hui`);
  if (counts.late > 0) parts.push(`${counts.late} en retard`);
  return `${parts.join(', ')}. Bonne journée !`;
}
